import React, { useEffect, useState } from 'react';
import { BookOpen, Calendar, ExternalLink, RefreshCw, Mail, ArrowRight, Building2, GraduationCap, Coins } from 'lucide-react';

interface BlogPost {
  title: string;
  date: string;
  source: string;
  url: string;
  summary: string;
  category: 'research' | 'education' | 'culture' | 'innovation';
  icon: JSX.Element;
}

export function BlogFeed() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    const fetchFeeds = async () => {
      try {
        setLoading(true);
        // In a real implementation, this would fetch from your RSS aggregator service
        const mockPosts: BlogPost[] = [
          {
            title: "Knut och Alice Wallenbergs Stiftelse satsar 3,7 miljarder på kvantteknologi",
            date: "2025-03-28",
            source: "Wallenbergstiftelserna",
            url: "https://kaw.wallenberg.org/",
            summary: "Sveriges största privata forskningsfinansiär investerar i ett nytt 10-årigt program för kvantteknologi. Programmet omfattar både grundforskning och tillämpad forskning med målet att positionera Sverige som ledande inom området.",
            category: 'research',
            icon: <GraduationCap className="w-8 h-8" />
          },
          {
            title: "Postkodstiftelsen lanserar nytt program för digital inkludering",
            date: "2025-03-30",
            source: "Postkodstiftelsen",
            url: "https://postkodstiftelsen.se/",
            summary: "Med 100 miljoner kronor i potten ska programmet minska det digitala utanförskapet i Sverige. Särskilt fokus ligger på äldre och socioekonomiskt utsatta grupper.",
            category: 'education',
            icon: <Building2 className="w-8 h-8" />
          },
          {
            title: "Kulturstiftelsen utökar stödet till fria kulturutövare",
            date: "2025-03-25",
            source: "Kulturstiftelsen",
            url: "https://www.kulturradet.se/",
            summary: "Efter pandemins långvariga effekter på kultursektorn utökar stiftelsen sitt stöd med ytterligare 75 miljoner kronor. Ansökningsperioden öppnar 15 april och stänger 15 maj 2025.",
            category: 'culture',
            icon: <Coins className="w-8 h-8" />
          },
          {
            title: "Innovationsstiftelsen och Microsoft i samarbete för AI-startups",
            date: "2025-03-29",
            source: "Innovationsstiftelsen",
            url: "https://www.vinnova.se/",
            summary: "Ett nytt acceleratorprogram för AI-startups lanseras med 50 miljoner i riskkapital och tillgång till Microsofts molntjänster. Programmet riktar sig särskilt till startups inom hållbarhet och hälsa.",
            category: 'innovation',
            icon: <Building2 className="w-8 h-8" />
          },
          {
            title: "Mistra investerar 400 miljoner i cirkulär ekonomi",
            date: "2025-03-26",
            source: "Mistra",
            url: "https://www.mistra.org/",
            summary: "Stiftelsen för miljöstrategisk forskning satsar på ett nytt forskningsprogram för cirkulär ekonomi. Programmet ska pågå i åtta år och involvera både akademi, näringsliv och offentlig sektor.",
            category: 'research',
            icon: <GraduationCap className="w-8 h-8" />
          },
          {
            title: "Nya Arvsfonden delar ut rekordbelopp till barn- och ungdomsprojekt",
            date: "2025-03-31",
            source: "Allmänna Arvsfonden",
            url: "https://www.arvsfonden.se/",
            summary: "1,2 miljarder kronor fördelas till 350 projekt runt om i landet. Fokusområden i år är psykisk hälsa, integration och tillgänglig fritid för barn med funktionsnedsättningar.",
            category: 'education',
            icon: <Coins className="w-8 h-8" />
          }
        ];
        setPosts(mockPosts);
      } catch (err) {
        setError('Kunde inte ladda senaste nyheterna');
      } finally {
        setLoading(false);
      }
    };

    fetchFeeds();
    const interval = setInterval(fetchFeeds, 300000); // Refresh every 5 minutes
    return () => clearInterval(interval);
  }, []);

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setSubscribeStatus('success');
    setTimeout(() => setSubscribeStatus('idle'), 3000);
    setEmail('');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F2C1D1] to-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <RefreshCw className="w-8 h-8 text-[#A3C1DA] animate-spin mx-auto" />
          <p className="mt-4 text-gray-600">Laddar senaste nyheterna...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F2C1D1] to-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center text-red-600">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F2C1D1] to-white py-16 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center p-2 bg-[#A3C1DA] rounded-full mb-4">
            <BookOpen className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Stiftelse Nyheter
          </h1>
          <p className="text-gray-600">
            Senaste nytt från stiftelsevärlden - uppdateras dagligen
          </p>
        </div>

        {/* Newsletter Subscription */}
        <div className="bg-gradient-to-r from-[#A3C1DA] to-[#F2C1D1] rounded-2xl shadow-xl p-8 mb-12 text-white">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <Mail className="w-12 h-12 text-white/90" />
              <div>
                <h2 className="text-xl font-bold mb-2">Nyhetsbrev</h2>
                <p className="text-white/90">
                  Få veckovisa uppdateringar om nya utlysningar, deadlines och stiftelsenytt
                </p>
              </div>
            </div>
            <form onSubmit={handleSubscribe} className="flex-shrink-0 w-full md:w-auto">
              <div className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Din e-postadress"
                  className="px-4 py-2 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 
                           placeholder-white/60 text-white w-full md:w-auto focus:outline-none 
                           focus:ring-2 focus:ring-white/50"
                  required
                />
                <button
                  type="submit"
                  className="px-6 py-2 bg-white text-[#A3C1DA] rounded-lg font-semibold 
                           hover:bg-[#F2C1D1]/10 hover:text-white transition-colors flex items-center gap-2"
                >
                  Prenumerera
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
              {subscribeStatus === 'success' && (
                <p className="mt-2 text-sm text-white/90">
                  ✓ Tack för din prenumeration!
                </p>
              )}
            </form>
          </div>
        </div>

        <div className="space-y-8">
          {posts.map((post, index) => (
            <article 
              key={index}
              className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 p-3 bg-[#F2C1D1]/10 rounded-lg">
                  <div className="text-[#A3C1DA]">
                    {post.icon}
                  </div>
                </div>
                <div className="flex-grow">
                  <h2 className="text-xl font-bold text-gray-900 mb-2 hover:text-[#A3C1DA] transition-colors">
                    <a href={post.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                      {post.title}
                      <ExternalLink className="w-4 h-4 opacity-50" />
                    </a>
                  </h2>
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {post.date}
                    </span>
                    <span className="text-[#A3C1DA] font-medium">
                      {post.source}
                    </span>
                  </div>
                  <p className="text-gray-600 leading-relaxed">
                    {post.summary}
                  </p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}